import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, AlertTriangle, Download, CheckCircle2, Calendar, XCircle } from 'lucide-react';
import { downloadBlob, isIOS } from '../../utils/downloadBlob';

const API = process.env.REACT_APP_BACKEND_URL;

const WT_COLOR = {
  verbal: 'bg-yellow-100 text-yellow-700', written: 'bg-orange-100 text-orange-700',
  written2: 'bg-orange-100 text-orange-800', final: 'bg-red-100 text-red-700',
};
const fmt = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };
const fmtDT = (d) => { try { return new Date(d).toLocaleString('en-GB'); } catch { return d; } };

const PortalWarningLetters = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [objectOpenId, setObjectOpenId] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/employee-portal/my-warnings`, { headers });
      setItems(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/employee-portal/login');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (w) => {
    try {
      const r = await axios.get(`${API}/api/employee-portal/my-warnings/${w.warning_id}/pdf`, { headers, responseType: 'blob' });
      downloadBlob(r.data, `Warning_Letter_${w.issue_date || ''}.pdf`, 'application/pdf');
      toast({ title: isIOS() ? 'PDF opened' : 'Downloaded', description: isIOS() ? 'Tap Share to save or print.' : undefined });
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
  };

  const accept = async (w) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/employee-portal/my-warnings/${w.warning_id}/accept`, { note: '' }, { headers });
      toast({ title: 'Accepted', description: 'Your acceptance has been recorded.' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const object = async (w) => {
    if (!reason.trim()) { toast({ title: 'A reason is required to object', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/employee-portal/my-warnings/${w.warning_id}/object`, { reason }, { headers });
      toast({ title: 'Objection recorded', description: 'Your manager and HR have been notified.' });
      setObjectOpenId(null); setReason(''); load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="portal-warning-letters">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>
      <h1 className="text-xl font-bold flex items-center gap-2 mb-1">
        <AlertTriangle className="h-5 w-5 text-yellow-600" /> Warning Letters
      </h1>
      <p className="text-sm text-gray-500 mb-4">Review your disciplinary letters and acknowledge receipt. Acknowledgement is timestamped.</p>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">You have no warning letters. 🎉</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map((w) => {
            const status = w.response_status || (w.acknowledged ? 'accepted' : 'pending');
            const borderCls = status === 'accepted' ? 'border-emerald-200' : status === 'objected' ? 'border-red-300' : 'border-yellow-300';
            return (
            <Card key={w.warning_id} className={borderCls} data-testid={`portal-warning-${w.warning_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${WT_COLOR[w.warning_type] || 'bg-gray-100 text-gray-700'} text-xs`}>{w.warning_type_label}</Badge>
                    <span className="text-sm text-gray-600 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmt(w.issue_date)}</span>
                    {w.suspension_enabled && w.suspension_days && (
                      <Badge className="bg-red-600 text-xs">Suspension {w.suspension_days}d</Badge>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => download(w)} data-testid={`portal-warning-pdf-${w.warning_id}`}>
                    <Download className="h-4 w-4 mr-1" /> PDF
                  </Button>
                </div>
                {w.subject && <p className="text-sm font-semibold text-gray-800 mb-1">{w.subject}</p>}
                {w.issued_by && <p className="text-[11px] text-gray-400 mb-1">Issued by {w.issued_by}</p>}
                {(w.offense_types?.length > 0 || w.custom_offenses?.length > 0) && (
                  <p className="text-xs text-gray-500 mb-1">Offenses: {[...(w.offense_types || []).map(o => o.replace(/_/g, ' ')), ...(w.custom_offenses || [])].join(', ')}</p>
                )}
                <p className="text-sm text-gray-700">{w.description}</p>

                {status === 'accepted' ? (
                  <div className="mt-3 flex items-center gap-1.5 text-sm text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" /> Accepted on {fmtDT(w.accepted_at || w.acknowledged_at)}
                    {w.auto_accepted ? <span className="text-gray-400 italic"> — auto-accepted (no response within 5 days)</span> : null}
                  </div>
                ) : status === 'objected' ? (
                  <div className="mt-3 text-sm text-red-600">
                    <div className="flex items-center gap-1.5"><XCircle className="h-4 w-4" /> Objected on {fmtDT(w.objected_at)}</div>
                    {w.objection_reason ? <p className="text-gray-600 italic mt-0.5">“{w.objection_reason}”</p> : null}
                  </div>
                ) : objectOpenId === w.warning_id ? (
                  <div className="mt-3 space-y-2 bg-red-50 p-3 rounded-lg border border-red-200">
                    <p className="text-xs text-gray-600">Please state your reason for objecting to this warning. Your manager and HR will be notified.</p>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                      placeholder="Reason for objection (required)…" data-testid="object-reason-input" />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => { setObjectOpenId(null); setReason(''); }}>Cancel</Button>
                      <Button size="sm" disabled={busy} className="bg-red-600 hover:bg-red-700"
                        onClick={() => object(w)} data-testid={`confirm-object-${w.warning_id}`}>
                        {busy ? 'Saving…' : 'Submit Objection'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    {w.auto_accept_due && (
                      <p className="text-[11px] text-amber-600 mb-2">Please respond — this warning is auto-accepted on {fmt(w.auto_accept_due)} if no action is taken.</p>
                    )}
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy}
                        onClick={() => accept(w)} data-testid={`accept-warning-${w.warning_id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Accept
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => { setObjectOpenId(w.warning_id); setReason(''); }} data-testid={`object-warning-${w.warning_id}`}>
                        <XCircle className="h-4 w-4 mr-1" /> Object
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PortalWarningLetters;
